import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BookOpen, Users, Play, Clock, Eye, CheckCircle, TrendingUp, 
  BarChart3, Award, Target, Calendar
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EducatorDashboard = ({ user }) => {
  const [stats, setStats] = useState({
    total_programs: 0,
    total_sessions: 0,
    total_enrollments: 0,
    total_views: 0,
    total_completions: 0,
    avg_time_spent: 0,
    completion_rate: 0
  });
  const [recentPrograms, setRecentPrograms] = useState([]);
  const [topPrograms, setTopPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEducatorStats();
  }, []);

  const fetchEducatorStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, programsRes] = await Promise.all([
        axios.get(`${API_URL}/educator/stats`, { headers }),
        axios.get(`${API_URL}/educator/programs`, { headers })
      ]);

      setStats(statsRes.data);
      setRecentPrograms(programsRes.data.recent || []);
      setTopPrograms(programsRes.data.top_performing || []);
    } catch (error) {
      console.error('Failed to fetch educator stats:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-area">
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
          Educator Dashboard
        </h1>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
          Track your content performance and learner engagement
        </p>
      </div>

      {/* Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px', 
        marginBottom: '32px' 
      }}>
        {/* Programs Created */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ 
              width: '48px', height: '48px', borderRadius: '12px', 
              background: 'rgba(99, 102, 241, 0.1)', 
              display: 'flex', alignItems: 'center', justifyContent: 'center' 
            }}>
              <BookOpen size={24} style={{ color: '#6366F1' }} />
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {stats.total_programs}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Programs Created</div>
            </div>
          </div>
        </div>

        {/* Total Sessions */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ 
              width: '48px', height: '48px', borderRadius: '12px', 
              background: 'rgba(16, 185, 129, 0.1)', 
              display: 'flex', alignItems: 'center', justifyContent: 'center' 
            }}>
              <Play size={24} style={{ color: '#10B981' }} />
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {stats.total_sessions}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Total Sessions</div>
            </div>
          </div>
        </div>

        {/* Total Views */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ 
              width: '48px', height: '48px', borderRadius: '12px', 
              background: 'rgba(59, 130, 246, 0.1)', 
              display: 'flex', alignItems: 'center', justifyContent: 'center' 
            }}>
              <Eye size={24} style={{ color: '#3B82F6' }} />
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {stats.total_views}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Total Views</div>
            </div>
          </div>
        </div>

        {/* Completions */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ 
              width: '48px', height: '48px', borderRadius: '12px', 
              background: 'rgba(245, 158, 11, 0.1)', 
              display: 'flex', alignItems: 'center', justifyContent: 'center' 
            }}>
              <CheckCircle size={24} style={{ color: '#F59E0B' }} />
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>
                {stats.total_completions}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>Completions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        marginBottom: '32px' 
      }}>
        {/* Completion Rate */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Completion Rate
            </h3>
            <Target size={20} style={{ color: 'var(--text-tertiary)' }} />
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700', color: '#10B981', marginBottom: '8px' }}>
            {stats.completion_rate}%
          </div>
          <div style={{ 
            height: '8px', 
            background: 'var(--bg-secondary)', 
            borderRadius: '4px', 
            overflow: 'hidden' 
          }}>
            <div style={{ 
              width: `${stats.completion_rate}%`, 
              height: '100%', 
              background: '#10B981', 
              borderRadius: '4px',
              transition: 'width 0.5s ease'
            }} />
          </div>
        </div>

        {/* Avg Time Spent */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Avg. Time per Session
            </h3>
            <Clock size={20} style={{ color: 'var(--text-tertiary)' }} />
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700', color: '#6366F1' }}>
            {formatTime(stats.avg_time_spent)}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            Average learner engagement
          </div>
        </div>

        {/* Total Learners */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Total Learners
            </h3>
            <Users size={20} style={{ color: 'var(--text-tertiary)' }} />
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700', color: '#3B82F6' }}>
            {stats.total_enrollments}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            Unique learners engaged
          </div>
        </div>
      </div>

      {/* Programs Sections */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
        {/* Recent Programs */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Recent Programs
            </h3>
            <Calendar size={20} style={{ color: 'var(--text-tertiary)' }} />
          </div>
          {recentPrograms.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-tertiary)' }}>
              <BookOpen size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No programs created yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {recentPrograms.map((program, idx) => (
                <div key={idx} style={{ 
                  padding: '12px 16px', 
                  background: 'var(--bg-secondary)', 
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '14px' }}>
                      {program.title}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                      {program.sessions_count || 0} sessions • {program.pillar}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: program.status === 'published' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                    color: program.status === 'published' ? '#10B981' : '#F59E0B',
                    textTransform: 'capitalize'
                  }}>
                    {program.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Performing */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Top Performing
            </h3>
            <TrendingUp size={20} style={{ color: 'var(--text-tertiary)' }} />
          </div>
          {topPrograms.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-tertiary)' }}>
              <BarChart3 size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No engagement data yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {topPrograms.map((program, idx) => (
                <div key={idx} style={{ 
                  padding: '12px 16px', 
                  background: 'var(--bg-secondary)', 
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '14px' }}>
                      {idx + 1}. {program.title}
                    </div>
                    <Award size={16} style={{ color: idx === 0 ? '#F59E0B' : 'var(--text-tertiary)' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    <span><Eye size={12} style={{ marginRight: '4px' }} />{program.views || 0} views</span>
                    <span><CheckCircle size={12} style={{ marginRight: '4px' }} />{program.completions || 0} done</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EducatorDashboard;
